import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Upload, Link as LinkIcon, Image } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Live Cats', 'Cat Food', 'Accessories', 'Grooming', 'Litter & Hygiene', 'Health & Wellness'];
const empty = { name: '', description: '', price: 0, mrp: null, category: 'Cat Food', image_url: '', image_urls: [], stock: 0, wholesale_price: null, min_wholesale_qty: null, is_featured: false, is_active: true, tags: [], cod_available: false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetch = () => { setLoading(true); axios.get(`${API}/admin/products?limit=100`, { withCredentials: true }).then(r => setProducts(r.data.products)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditProduct(null); setForm({ ...empty }); setTagsInput(''); setDialogOpen(true); };
  const openEdit = (p) => { setEditProduct(p); setForm({ ...p, image_urls: p.image_urls || (p.image_url ? [p.image_url] : []), wholesale_price: p.wholesale_price || null, min_wholesale_qty: p.min_wholesale_qty || null, mrp: p.mrp || null }); setTagsInput((p.tags || []).join(', ')); setDialogOpen(true); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, GIF files allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    if ((form.image_urls || []).length >= 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        image_urls: [...(prev.image_urls || []), data.image_url],
        image_url: prev.image_url || data.image_url  // first image becomes the main image
      }));
      toast.success('Image uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (idx) => {
    setForm(prev => {
      const newUrls = prev.image_urls.filter((_, i) => i !== idx);
      return {
        ...prev,
        image_urls: newUrls,
        image_url: newUrls[0] || ''  // update main image to first remaining
      };
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.category || form.price <= 0) { toast.error('Fill required fields'); return; }
    setSaving(true);
    const payload = {
      ...form,
      tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
      wholesale_price: form.wholesale_price || null,
      min_wholesale_qty: form.min_wholesale_qty || null,
      image_url: form.image_urls?.[0] || form.image_url || '',
      image_urls: form.image_urls || []
    };
    try {
      if (editProduct) await axios.put(`${API}/admin/products/${editProduct.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      toast.success(editProduct ? 'Updated' : 'Created'); setDialogOpen(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true }); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  // Helper to get display image
  const getDisplayImage = (p) => (p.image_urls && p.image_urls.length > 0) ? p.image_urls[0] : p.image_url;

  return (
    <AdminLayout active="/admin/products">
      <div data-testid="admin-products">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-dark-brown">Products</h1>
          <Button onClick={openNew} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
        </div>
        {loading ? <div className="flex justify-center py-20"><div className="spinner"></div></div> : (
          <div className="bg-white rounded-xl border border-border-custom overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table"><thead><tr className="border-b border-border-custom bg-pale/30">
                <th className="text-left font-body text-xs text-light-brown uppercase p-4">Product</th>
                <th className="text-left font-body text-xs text-light-brown uppercase p-4">Category</th>
                <th className="text-right font-body text-xs text-light-brown uppercase p-4">Price</th>
                <th className="text-center font-body text-xs text-light-brown uppercase p-4">COD</th>
                <th className="text-center font-body text-xs text-light-brown uppercase p-4">Photos</th>
                <th className="text-right font-body text-xs text-light-brown uppercase p-4">Stock</th>
                <th className="text-right font-body text-xs text-light-brown uppercase p-4">Actions</th>
              </tr></thead><tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-b border-border-custom last:border-0 hover:bg-cream/30">
                    <td className="p-4"><div className="flex items-center gap-3"><img src={getDisplayImage(p)} alt="" className="w-10 h-10 rounded-lg object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=100'; }} /><div><div className="font-body text-sm font-medium text-dark-brown">{p.name}</div>{p.is_featured && <span className="text-xs text-rust font-body">Featured</span>}</div></div></td>
                    <td className="p-4 font-body text-sm text-light-brown">{p.category}</td>
                    <td className="p-4 text-right font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'₹'}{p.price?.toLocaleString('en-IN')}</td>
                    <td className="p-4 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-body ${p.cod_available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.cod_available ? 'Yes' : 'No'}</span></td>
                    <td className="p-4 text-center"><span className="px-2 py-0.5 rounded-full text-xs font-body bg-blue-50 text-blue-600">{(p.image_urls || []).length || (p.image_url ? 1 : 0)}</span></td>
                    <td className="p-4 text-right font-body text-sm">{p.stock}</td>
                    <td className="p-4 text-right"><div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-pale" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4 text-mid-brown" /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">{editProduct ? 'Edit' : 'New'} Product</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label className="font-body text-sm">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-cream/50" data-testid="product-name-input" /></div>
              <div><Label className="font-body text-sm">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 bg-cream/50" rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-body text-sm">Price *</Label><Input type="number" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value)||0})} className="mt-1 bg-cream/50" /></div>
                <div><Label className="font-body text-sm">MRP (Striked Price)</Label><Input type="number" value={form.mrp||''} onChange={e => setForm({...form, mrp: parseFloat(e.target.value)||null})} className="mt-1 bg-cream/50" /></div>
              </div>
              <div><Label className="font-body text-sm">Category *</Label><Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger className="mt-1 bg-cream/50"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>

              {/* Multi-Image Upload Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-body text-sm">Product Images <span className="text-light-brown text-xs">(up to 5)</span></Label>
                  <span className="text-xs font-body text-mid-brown">{(form.image_urls || []).length}/5</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {(form.image_urls || []).map((url, idx) => (
                    <div key={idx} className="relative group w-20 h-20">
                      <img src={url} alt="" className="w-full h-full object-cover rounded-lg border border-border-custom" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=100'; }} />
                      {idx === 0 && (
                        <span className="absolute -top-1.5 -left-1.5 bg-rust text-cream text-[9px] font-bold px-1.5 py-0.5 rounded-full z-10">MAIN</span>
                      )}
                      <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10">✕</button>
                    </div>
                  ))}
                  {(form.image_urls || []).length < 5 && (
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 border-2 border-dashed border-border-custom rounded-lg flex flex-col items-center justify-center text-mid-brown hover:bg-pale hover:border-rust transition-colors"
                      data-testid="upload-product-img-btn"
                    >
                      {uploading ? <div className="spinner w-4 h-4"></div> : <><Upload className="w-5 h-5 mb-1" /><span className="text-[10px] font-body">Upload</span></>}
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                    data-testid="image-file-input"
                  />
                </div>
                <p className="font-body text-xs text-light-brown mt-2">JPG, PNG, WebP, GIF · Max 10MB each · First image is the main product photo</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-body text-sm">Stock</Label><Input type="number" value={form.stock} onChange={e => setForm({...form, stock: parseInt(e.target.value)||0})} className="mt-1 bg-cream/50" /></div>
                <div><Label className="font-body text-sm">Tags</Label><Input value={tagsInput} onChange={e => setTagsInput(e.target.value)} className="mt-1 bg-cream/50" placeholder="comma separated" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-body text-sm">Wholesale Price</Label><Input type="number" value={form.wholesale_price||''} onChange={e => setForm({...form, wholesale_price: parseFloat(e.target.value)||null})} className="mt-1 bg-cream/50" /></div>
                <div><Label className="font-body text-sm">Min Wholesale Qty</Label><Input type="number" value={form.min_wholesale_qty||''} onChange={e => setForm({...form, min_wholesale_qty: parseInt(e.target.value)||null})} className="mt-1 bg-cream/50" /></div>
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={v => setForm({...form, is_featured: v})} /><Label className="font-body text-sm">Featured</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label className="font-body text-sm">Active</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.cod_available} onCheckedChange={v => setForm({...form, cod_available: v})} data-testid="cod-toggle" /><Label className="font-body text-sm text-green-700 font-medium">COD Available</Label></div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-rust hover:bg-rust/90 text-cream font-body rounded-full" data-testid="save-product-btn">{saving ? 'Saving...' : editProduct ? 'Update' : 'Create'}</Button>
                <Button onClick={() => setDialogOpen(false)} variant="outline" className="font-body rounded-full">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
